const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/triemli/eCheckin/sections/Versicherung.tsx';
import * as React from 'react';
import { forwardRef } from 'react';
import BehandlungsgrundField from 'App/components/eCheckin/components/fields/metakat/BehandlungsgrundField';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import GrundversicherungField from 'App/config/triemli/eCheckin/sections/fields/GrundversicherungField';
import UnfallArtField from './fields/UnfallArtField';
import UnfallVersicherungField from 'App/config/triemli/eCheckin/sections/fields/UnfallVersicherungField';
import VersicherungsangabeField from 'App/config/triemli/eCheckin/sections/fields/VersicherungsangabeField';
import VersicherungsdeckungField from './fields/VersicherungsdeckungField';
import _ from 'lodash';
import moment from 'moment/moment';
import textMap from '../translations';
import textMapCommon from 'App/config/common/translations';
import { Alert } from 'App/components/Alert/Alert';
import { AnstellungField } from 'App/components/eCheckin/components/fields/metakat/AnstellungField';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { CIS4mDatePicker } from '@cistec/common_web/modules/CIS4m/components/CIS4mDatePicker';
import { CIS4mPhoneNumberField } from '@cistec/common_web/modules/CIS4m/components/CIS4mPhoneNumberField';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { createFieldPath, fieldValueIsNot } from 'App/components/eCheckin/utils';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { useText } from '@cistec/common_web/utils/intlUtils';
import ZusatzversicherungField from 'App/config/triemli/eCheckin/sections/fields/ZusatzversicherungField';
import CIS4mSingleCheckbox from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { CIS4mMaskedTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mMaskedTextField';
import { AHVField } from 'App/components/eCheckin/components/fields/AHVField';
import WunschleistungField from './fields/WunschleistungField';
import { useIntl } from 'react-intl';
import { Divider } from '@mui/material';

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const { formatMessage } = useIntl();
    const title = useText(props.title);
    const formData = useCIS4mData();

    const PATH_BEHANDLUNGSGRUND = createFieldPath(path, 'reason');
    const PATH_TWIN_BIRTH = createFieldPath(path, 'twinBirth');
    const PATH_DUE_DATE = createFieldPath(path, 'dueDate');
    const PATH_ACCIDENT_TYPE = createFieldPath(path, 'accident');
    const PATH_ACCIDENT_DATE = createFieldPath(path, 'accidentDate');
    const PATH_EMPLOYMENT = createFieldPath(path, 'employment');
    const PATH_EMPLOYER = createFieldPath(path, 'employer');
    const PATH_EMPLOYER_STREET_AND_NUMBER = createFieldPath(path, 'empStreetAndNumber');
    const PATH_EMPLOYER_PLZ = createFieldPath(path, 'empPlz');
    const PATH_EMPLOYER_CITY = createFieldPath(path, 'empOrt');
    const PATH_EMPLOYER_TEL = createFieldPath(path, 'empTel');
    const PATH_ACCIDENT_INSURANCE_UVG = createFieldPath(path, 'accInsuranceUvg');
    const PATH_ACCIDENT_INSURANCE = createFieldPath(path, 'accInsurance');
    const PATH_OTHER_ACCIDENT_INSURANCE = createFieldPath(path, 'otherAccInsurance');
    const PATH_POLICYNUMBER = createFieldPath(path, 'policyNumber');
    const PATH_REF_NUMBER = createFieldPath(path, 'refNumber');
    const PATH_INFO_INSURANCE = createFieldPath(path, 'infoInsurance');
    const PATH_BASIC_INSURANCE = createFieldPath(path, 'basicInsurance');
    const PATH_OTHER_BASIC_INSURANCE = createFieldPath(path, 'otherBasicInsurance');
    const PATH_VEKA = createFieldPath(path, 'veka');
    const PATH_INSURANCE_NUMBER = createFieldPath(path, 'insuranceNumber');
    const PATH_INSURANCE_COVERAGE = createFieldPath(path, 'insuranceCoverage');
    const PATH_INSURANCE_ADD = createFieldPath(path, 'insucanceAdd');
    const PATH_VVG = createFieldPath(path, 'vvg');
    const PATH_VVG_NAME = createFieldPath(path, 'vvgName');
    const PATH_OTHER_VVG_NAME = createFieldPath(path, 'otherVvg');
    const PATH_NO_VEKA = createFieldPath(path, 'noVeka');
    const PATH_VVG_VEKA = createFieldPath(path, 'vvgVeka');
    const PATH_VVG_INSURANCE_NUMBER = createFieldPath(path, 'vvgInsuranceNumber');
    const PATH_DESIRED_SERVICE = createFieldPath(path, 'desiredService');
    const PATH_COST_CONFIRMATION = createFieldPath(path, 'costConfirmation');

    const isBirth = _.get(formData, PATH_BEHANDLUNGSGRUND) === 'birth';
    const isAccident = _.get(formData, PATH_BEHANDLUNGSGRUND) === 'accident';
    const isAccidentOrSickness = !fieldValueIsNot(formData, PATH_BEHANDLUNGSGRUND, ['accident', 'sickness']);
    const isSelfEmployed = _.get(formData, PATH_EMPLOYMENT) === 'self-employed';
    const isEmployed = _.get(formData, PATH_EMPLOYMENT) === 'employed';
    const isNotEmployed = fieldValueIsNot(formData, PATH_EMPLOYMENT, ['employed', 'self-employed', 'rav']);
    const isUvg = _.get(formData, PATH_ACCIDENT_INSURANCE_UVG) === '1';
    const isOtherAccInsurance = _.get(formData, PATH_ACCIDENT_INSURANCE) === 'other';
    const isOtherBasicInsurance = _.get(formData, PATH_BASIC_INSURANCE) === 'other';
    const isOtherVvg = _.get(formData, PATH_VVG_NAME) === 'other';
    const isHpOrP = !fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, ['HP', 'P']);
    const isHp = _.get(formData, PATH_INSURANCE_COVERAGE) === 'HP';
    const isA = !fieldValueIsNot(formData, PATH_INSURANCE_COVERAGE, ['A', 'ACH']);
    const isAllgemeinWohnkanton = _.get(formData, PATH_INSURANCE_COVERAGE) === 'A';
    const isFlexCoverage = _.get(formData, PATH_INSURANCE_COVERAGE) === 'FLEX';
    const isWishHPOrP = !fieldValueIsNot(formData, PATH_DESIRED_SERVICE, ['HP', 'P']);
    const isWishHP = _.get(formData, PATH_DESIRED_SERVICE) === 'HP';
    const isInfoInsurance = _.get(formData, PATH_INFO_INSURANCE);
    const isAuslandOhneChVersicherung = _.get(formData, PATH_INFO_INSURANCE) === 'auslandOhneChVersicherung';
    const isVvg = _.get(formData, PATH_VVG) === '1';
    const isNoVeka = _.get(formData, PATH_NO_VEKA) === '1';

    const showEmployerInfo = !isNotEmployed;
    const showAccidentInsurance = isEmployed || isUvg;
    const showVvgVeka = !isNoVeka && !isVvg;
    const showCostConfirmation = isHpOrP || (isFlexCoverage && isWishHPOrP);
    const showAccidentOrSicknessInfo = isAccidentOrSickness && (isHp || isA || (isWishHP && isFlexCoverage));
    const showBirthInfo = isBirth && (isHp || isWishHP || isA);

    const eCheckinSectionRef = useCIS4m(ref);

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <BehandlungsgrundField label={textMap.behandlungsgrund} path={PATH_BEHANDLUNGSGRUND} required />
            <CIS4mBlock hidden={!isBirth}>
                <YesNoField label={textMap.twinBirth} path={PATH_TWIN_BIRTH} required />
                <CIS4mDatePicker label={textMap.dueDate} path={PATH_DUE_DATE} minDate={moment()} required />
            </CIS4mBlock>
            <CIS4mBlock hidden={!isAccident}>
                <CIS4mDatePicker label={textMap.accidentDate} path={PATH_ACCIDENT_DATE} maxDate={moment()} required />
                <UnfallArtField label={textMap.accidentType} path={PATH_ACCIDENT_TYPE} required />
                <AnstellungField label={textMap.employment} path={PATH_EMPLOYMENT} required />
                <CIS4mBlock hidden={!showEmployerInfo}>
                    <Alert color={'info'} hideIcon>
                        {useText(textMap.arbeitgeber)}
                    </Alert>
                    <CIS4mTextField path={PATH_EMPLOYER} label={textMap.arbeitgeber} required />
                    <CIS4mTextField path={PATH_EMPLOYER_STREET_AND_NUMBER} label={textMap.streetAndNumber} required />
                    <CIS4mTextField path={PATH_EMPLOYER_PLZ} label={textMapCommon.plz} required />
                    <CIS4mTextField path={PATH_EMPLOYER_CITY} label={textMapCommon.city} required />
                    <CIS4mPhoneNumberField label={textMapCommon.telGeneral} path={PATH_EMPLOYER_TEL} required />
                </CIS4mBlock>
                <YesNoField
                    path={PATH_ACCIDENT_INSURANCE_UVG}
                    label={textMap.accInsuranceUvg}
                    hidden={!isSelfEmployed}
                    required
                />
                <UnfallVersicherungField
                    label={textMap.accidentInsurance}
                    path={PATH_ACCIDENT_INSURANCE}
                    required
                    hidden={!showAccidentInsurance}
                />
                <CIS4mTextField
                    path={PATH_OTHER_ACCIDENT_INSURANCE}
                    label={textMap.accidentInsurance}
                    hidden={!isOtherAccInsurance}
                    required
                />
                <CIS4mBlock hidden={!showAccidentInsurance}>
                    <CIS4mTextField path={PATH_POLICYNUMBER} label={textMap.policyNumber} required />
                    <CIS4mTextField path={PATH_REF_NUMBER} label={textMap.refNumber} required />
                </CIS4mBlock>
            </CIS4mBlock>
            <Divider />
            <VersicherungsangabeField path={PATH_INFO_INSURANCE} label={textMap.infoInsurance} required />
            <CIS4mBlock hidden={!isInfoInsurance}>
                <GrundversicherungField
                    path={PATH_BASIC_INSURANCE}
                    hidden={isAuslandOhneChVersicherung}
                    label={textMap.basicInsurance}
                    required
                />
                <CIS4mTextField
                    label={textMap.basicInsurance}
                    path={PATH_OTHER_BASIC_INSURANCE}
                    hidden={!isOtherBasicInsurance}
                    required
                />
                <CIS4mBlock hidden={isAuslandOhneChVersicherung}>
                    <CIS4mMaskedTextField
                        mask={'80756999999999999999'}
                        maxLength={20}
                        path={PATH_VEKA}
                        label={textMap.veka}
                        helperText={textMap.vekaHelperText}
                    />
                    <CIS4mTextField
                        path={PATH_INSURANCE_NUMBER}
                        label={textMap.insuranceNumber}
                        helperText={textMap.insuranceNumberHelperText}
                    />
                    <VersicherungsdeckungField
                        path={PATH_INSURANCE_COVERAGE}
                        label={textMap.insuranceCoverage}
                        required
                    />
                    <CIS4mTextField path={PATH_INSURANCE_ADD} label={textMap.insucanceAdd} />

                    <YesNoField path={PATH_VVG} label={textMap.vvg} hidden={isAllgemeinWohnkanton} required />
                    <ZusatzversicherungField path={PATH_VVG_NAME} label={textMap.vvgName} hidden={isVvg} required />
                    <CIS4mTextField path={PATH_OTHER_VVG_NAME} label={textMap.vvgName} hidden={!isOtherVvg} required />

                    <CIS4mSingleCheckbox label={textMap.noVeka} path={PATH_NO_VEKA} hidden={isVvg} />
                    <CIS4mMaskedTextField
                        mask={'80756999999999999999'}
                        maxLength={20}
                        path={PATH_VVG_VEKA}
                        label={textMap.vvgVeka}
                        helperText={textMap.vekaHelperText}
                        hidden={!showVvgVeka}
                        required
                    />
                    <AHVField
                        label={textMap.vvgInsuranceNumber}
                        path={PATH_VVG_INSURANCE_NUMBER}
                        helperText={textMap.insuranceNumberHelperText}
                        hidden={isVvg}
                    />
                </CIS4mBlock>
            </CIS4mBlock>
            <WunschleistungField
                path={PATH_DESIRED_SERVICE}
                label={textMap.upgradeCoverage}
                hidden={!isFlexCoverage}
                required
            />

            <YesNoField
                path={PATH_COST_CONFIRMATION}
                label={textMap.confirmCosts}
                hidden={!showCostConfirmation}
                required={true}
            />
            <CIS4mBlock hidden={!showAccidentOrSicknessInfo}>
                <Alert color={'info'} hideIcon>
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoGeneral) }} />
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoEndPart) }} />
                </Alert>
            </CIS4mBlock>
            <CIS4mBlock hidden={!showBirthInfo}>
                <Alert color={'info'} hideIcon>
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoBirth) }} />
                    {isHp || isWishHP ? (
                        <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoBirthDetailHP) }} />
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoBirthDetail) }} />
                    )}
                    <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.infoEndPart) }} />
                </Alert>
            </CIS4mBlock>
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
